import * as React from 'react';
import ResizeObserver from 'resize-observer-polyfill';
import _ from 'lodash';

import debugHook from './hookUtils';

const getInitialSize = () => {
    return {
        width: undefined,
        height: undefined,
    };
};

export default debugHook(useResizeObserver, 'useResizeObserver');

/**
 * Measures the size of a DOM element.
 * You can either pass in a `ref` or use the returned `ref` reference to select which DOM element to measure.
 * `width` and `height` are returned from the hook, and will trigger a render when they update.
 * You can also pass in an `onResize` callback to receive those values.
 *
 * @param {Object} options
 * @param {React.MutableRefObject} [options.ref] - Optional react reference to DOM element to measure size of
 * @param {Function} [options.onResize] - Optional callback to receive size changes
 * @returns {{width: Number, height: number, ref: React.MutableRefObject}}
 */
export function useResizeObserver({ref, onResize} = {}) {
    const internalRef = React.useRef();
    const targetRef = ref || internalRef;
    const targetElement = targetRef.current;

    const resizeObserverRef = React.useRef();
    const [size, setSize] = React.useState(getInitialSize);
    const previous = React.useRef(Object.assign({}, size));

    const onResizeRef = React.useRef();
    onResizeRef.current = onResize;

    React.useEffect(() => {
        if (!targetElement) {
            return undefined;
        }

        const handleResize = _.throttle(
            newSize => {
                if (typeof onResizeRef.current === 'function') {
                    onResizeRef.current(newSize);
                }
                setSize(newSize);
            },
            50,
            {maxWait: 200},
        );

        resizeObserverRef.current = new ResizeObserver(entries => {
            if (!Array.isArray(entries) || !entries.length) {
                return;
            }
            const [entry] = entries;

            const newWidth = Math.round(entry.contentRect.width);
            const newHeight = Math.round(entry.contentRect.height);

            if (previous.current.width !== newWidth || previous.current.height !== newHeight) {
                const newSize = {width: newWidth, height: newHeight};
                Object.assign(previous.current, newSize);
                _.defer(() => handleResize(newSize));
            }
        });

        resizeObserverRef.current.observe(targetElement);

        return () => {
            previous.current = getInitialSize();
            setSize(previous.current);
            if (targetElement.parentElement) {
                // element may have been removed from DOM
                resizeObserverRef.current.unobserve(targetElement);
            }
        };
    }, [targetElement]);

    return React.useMemo(() => Object.assign({ref: internalRef}, size), [size, internalRef]);
}
